/**
 * User permission utilities for role-based access control
 */

import type { UserRole } from '@taxassist/api-client';

// User role constants that map to Cognito User Groups
// Note: These should ideally be imported from a shared package (lib/shared/UserRoles.ts)
// but due to monorepo import constraints, we define them locally
export const USER_ROLES = {
  admin: 'admin' as UserRole,
  reviewer: 'reviewer' as UserRole,
  firm_admin: 'firm_admin' as UserRole,
  firm_user: 'firm_user' as UserRole,
} as const;

// User roles in order of hierarchy (highest to lowest)
export const USER_ROLE_HIERARCHY: UserRole[] = [USER_ROLES.admin, USER_ROLES.reviewer, USER_ROLES.firm_admin, USER_ROLES.firm_user];

/**
 * Check if a user role has permission to perform user management operations
 */
export function canManageUsers(userRole: UserRole): boolean {
  return userRole === USER_ROLES.admin || userRole === USER_ROLES.firm_admin;
}

/**
 * Check if a user can invite users with a specific role
 */
export function canInviteRole(currentUserRole: UserRole, targetRole: UserRole): boolean {
  // Admin can invite anyone except reviewer (reviewer is internal only)
  if (currentUserRole === USER_ROLES.admin) {
    return targetRole !== USER_ROLES.reviewer;
  }
  
  // Reviewer cannot invite anyone (read-only role)
  if (currentUserRole === USER_ROLES.reviewer) {
    return false;
  }
  
  // Firm admin can invite firm_admin and firm_user, but not admin or reviewer
  if (currentUserRole === USER_ROLES.firm_admin) {
    return targetRole === USER_ROLES.firm_admin || targetRole === USER_ROLES.firm_user;
  }
  
  // Firm users cannot invite anyone
  return false;
}

/**
 * Check if a user can remove another user based on roles
 */
export function canRemoveUser(currentUserRole: UserRole, targetUserRole: UserRole): boolean {
  // Admin can remove anyone except other admins and reviewers (both are internal roles)
  if (currentUserRole === USER_ROLES.admin) {
    return targetUserRole !== USER_ROLES.admin && targetUserRole !== USER_ROLES.reviewer;
  }
  
  // Reviewer cannot remove anyone (read-only role)
  if (currentUserRole === USER_ROLES.reviewer) {
    return false;
  }
  
  // Firm admin can remove firm users and other firm admins
  if (currentUserRole === USER_ROLES.firm_admin) {
    return targetUserRole === USER_ROLES.firm_user || targetUserRole === USER_ROLES.firm_admin;
  }
  
  // Firm users cannot remove anyone
  return false;
}

/**
 * Check if a user can edit another user's role
 */
export function canEditUserRole(currentUserRole: UserRole, targetUserRole: UserRole, isSelf: boolean): boolean {
  // Users cannot edit their own role
  if (isSelf) {
    return false;
  }
  
  // Admin can edit anyone's role except other admins and reviewers (internal roles)
  if (currentUserRole === USER_ROLES.admin) {
    return targetUserRole !== USER_ROLES.admin && targetUserRole !== USER_ROLES.reviewer;
  }
  
  // Reviewer cannot edit anyone's role (read-only role)
  if (currentUserRole === USER_ROLES.reviewer) {
    return false;
  }
  
  // Firm admin can edit firm users and other firm admins (but not themselves)
  if (currentUserRole === USER_ROLES.firm_admin) {
    return targetUserRole === USER_ROLES.firm_user || targetUserRole === USER_ROLES.firm_admin;
  }
  
  // Firm users cannot edit anyone's role
  return false;
}

/**
 * Get the maximum role a user can assign based on their current role
 */
export function getMaxAssignableRole(currentUserRole: UserRole): UserRole {
  if (currentUserRole === USER_ROLES.admin) {
    return USER_ROLES.admin;
  }
  
  if (currentUserRole === USER_ROLES.firm_admin) {
    return USER_ROLES.firm_admin;
  }
  
  return USER_ROLES.firm_user;
}

/**
 * Get available roles that a user can assign to others
 */
export function getAssignableRoles(currentUserRole: UserRole): UserRole[] {
  if (currentUserRole === USER_ROLES.admin) {
    // Admin can assign all external roles (not reviewer which is internal only)
    return [USER_ROLES.admin, USER_ROLES.firm_admin, USER_ROLES.firm_user];
  }
  
  if (currentUserRole === USER_ROLES.reviewer) {
    // Reviewer cannot assign any roles (read-only role)
    return [];
  }
  
  if (currentUserRole === USER_ROLES.firm_admin) {
    return [USER_ROLES.firm_admin, USER_ROLES.firm_user];
  }
  
  return [];
}

/**
 * Check if a role is higher in hierarchy than another role
 */
export function isHigherRole(role1: UserRole, role2: UserRole): boolean {
  const role1Index = USER_ROLE_HIERARCHY.indexOf(role1);
  const role2Index = USER_ROLE_HIERARCHY.indexOf(role2);
  
  return role1Index < role2Index;
}


/**
 * Get display name for a user role
 */
export function getRoleDisplayName(role: UserRole): string {
  switch (role) {
    case USER_ROLES.admin:
      return 'Ops';
    case USER_ROLES.reviewer:
      return 'Reviewer';
    case USER_ROLES.firm_admin:
      return 'Firm Admin';
    case USER_ROLES.firm_user:
      return 'Firm User';
    default:
      return 'Unknown';
  }
}

/**
 * Get role description for help text
 */
export function getRoleDescription(role: UserRole): string {
  switch (role) {
    case USER_ROLES.admin:
      return 'Full system access with ability to manage all firms and users';
    case USER_ROLES.reviewer:
      return 'Internal reviewer with cross-firm visibility but no management capabilities';
    case USER_ROLES.firm_admin:
      return 'Can manage assigned firms and their users';
    case USER_ROLES.firm_user:
      return 'Can create and manage jobs within assigned firms';
    default:
      return 'Unknown role';
  }
}

/**
 * Check if a user can view all jobs across firms (Admin and Reviewer only)
 */
export function canViewAllJobs(userRole: UserRole): boolean {
  return userRole === USER_ROLES.admin || userRole === USER_ROLES.reviewer;
}

/**
 * Check if a user role is internal (Admin or Reviewer)
 */
export function isInternalRole(userRole: UserRole): boolean {
  return userRole === USER_ROLES.admin || userRole === USER_ROLES.reviewer;
}

/**
 * Check if user has admin role
 */
export function isAdmin(userRole: UserRole): boolean {
  return userRole === USER_ROLES.admin;
}

/**
 * Check if a user can access Ops pages (Admin and Reviewer only)
 */
export function canAccessOps(userRole: UserRole): boolean {
  return userRole === USER_ROLES.admin || userRole === USER_ROLES.reviewer;
}

/**
 * Check if a user (including role and groups) can access Ops pages
 */
export function userCanAccessOps(user: { role?: string; groups?: string[] } | null): boolean {
  if (!user) return false;
  
  // Check by role
  if (user.role === USER_ROLES.admin || user.role === USER_ROLES.reviewer) {
    return true;
  }
  
  // Check by groups (for cases where user might be in reviewer group)
  if (user.groups?.includes(USER_ROLES.reviewer)) {
    return true;
  }
  
  return false;
}